import { LinesField, TextField } from "@/app/fields";
import { PROTOCOL, TRAFFIC_TYPE, toOptions, type Channel, type Customer, type DLRPolicy } from "@/app/master";
import { idOptions } from "@/app/queries";
import type { Errors } from "@/app/useDraft";
import { Field, Fieldset, Form, InputGroup, Input, RadioGroup, Select, Switch } from "@/ui";
import { passwordRule, type AccountDraft } from "./customerDraft";

interface Props {
  draft: AccountDraft; errors: Errors<AccountDraft>; set: <K extends keyof AccountDraft>(k: K, v: AccountDraft[K]) => void; isNew: boolean;
  customers?: Customer[]; channels?: Channel[]; policies?: DLRPolicy[];
}

/** 账号表单的字段，见 docs/03 3.2。口令只写不读：新建必填，更新留空表示不改。 */
export function AccountForm({ draft, errors, set, isNew, customers, channels, policies }: Props) {
  const currency = customers?.find((c) => String(c.id) === draft.customer_id)?.currency ?? "";
  return (
    <div className="ax-stack">
      <Fieldset title="接入">
        <Form columns={2}>
          <Field label="所属客户" required error={errors.customer_id}>
            <Select block options={idOptions(customers)} value={draft.customer_id || null} onChange={(v) => set("customer_id", v)} placeholder="选择客户" invalid={!!errors.customer_id} />
          </Field>
          <TextField id="a-name" label="名称" required value={draft.name} error={errors.name} onChange={(v) => set("name", v)} />
          <Field label="协议" required>
            <RadioGroup name="protocol" inline value={draft.protocol} onChange={(v) => set("protocol", v)} options={toOptions(PROTOCOL)} disabled={!isNew} />
          </Field>
          <Field label="消息类型" required>
            <RadioGroup name="traffic_type" inline value={draft.traffic_type} onChange={(v) => set("traffic_type", v)} options={toOptions(TRAFFIC_TYPE)} />
          </Field>
          <TextField id="a-system-id" label="system_id" required mono value={draft.system_id} error={errors.system_id} hint="SMPP system_id 或 HTTP api key id" onChange={(v) => set("system_id", v)} />
          <TextField id="a-password" label="口令" required={isNew} type="password" value={draft.password} error={errors.password} hint={isNew ? passwordRule(draft.protocol) : `留空表示不改；${passwordRule(draft.protocol)}`} onChange={(v) => set("password", v)} />
          <TextField id="a-tps" label="TPS" required type="number" mono value={draft.tps} error={errors.tps} onChange={(v) => set("tps", v)} />
          <TextField id="a-max-binds" label="最大 bind 数" required type="number" mono value={draft.max_binds} error={errors.max_binds} onChange={(v) => set("max_binds", v)} />
        </Form>
      </Fieldset>
      <Fieldset title="来源与 sender">
        <Form>
          <LinesField id="a-ip" label="IP 白名单" value={draft.ip_whitelist} error={errors.ip_whitelist} hint="每行一个 CIDR" onChange={(v) => set("ip_whitelist", v)} />
          <div className="ax-row"><Switch label="不限制来源 IP" checked={draft.ip_unrestricted} onChange={(on) => set("ip_unrestricted", on)} /><Switch label="仅允许 TLS" checked={draft.tls_required} onChange={(on) => set("tls_required", on)} /></div>
          <LinesField id="a-senders" label="允许的 sender" value={draft.allowed_senders} hint="每行一个，区分大小写；留空表示不限制（决策 D29）" onChange={(v) => set("allowed_senders", v)} />
        </Form>
      </Fieldset>
      <Fieldset title="额度与回执">
 <Field label="回执策略" hint="未绑定保持真实回执；策略只影响新消息，真实投递与计费不变。">
 <Select block ariaLabel="回执策略" options={[{value:"",label:"真实回执（默认）"}, ...(policies ?? []).map(p=>({value:String(p.id),label:p.name+(p.status==="disabled"?"（停用）":"")}))]} value={draft.dlr_policy_id} onChange={v=>set("dlr_policy_id",v)} />
 </Field>
        <Form columns={2}>
          <Field label="信用额度" required htmlFor="a-credit" error={errors.credit_limit} hint="余额下限为负的额度">
            <InputGroup addon={currency || "币种"}><Input id="a-credit" mono value={draft.credit_limit} invalid={!!errors.credit_limit} onChange={(e) => set("credit_limit", e.target.value)} /></InputGroup>
          </Field>
          <Field label="钉死通道" hint="运营用，留空按路由选择">
            <Select block options={[{ value: "", label: "不钉死" }, ...idOptions(channels)]} value={draft.pinned_channel_id} onChange={(v) => set("pinned_channel_id", v)} />
          </Field>
          <TextField id="a-webhook" label="回执 webhook" value={draft.webhook_url} hint="HTTP 客户的回执地址" onChange={(v) => set("webhook_url", v)} />
          <Switch label="允许 HTTP 回调" checked={draft.webhook_allow_http} onChange={(on) => set("webhook_allow_http", on)} />
        </Form>
      </Fieldset>
      {!isNew && <Switch label="启用" checked={draft.status === "active"} onChange={(on) => set("status", on ? "active" : "disabled")} />}
    </div>
  );
}
